import { ILogger, createLogger } from '@/lib/logger';
import { ENDPOINTS, EndpointKey } from '@/services/endpoints';
import { isServer } from '@/services/env.service';
import { ClientApiService } from '@/services/http/ClientApiService';
import { ServerApiService } from '@/services/http/ServerApiService';

import { ApiResponse, QueryParams, RequestOptions } from '../http/BaseHttpClient';

let defaultBackend: IRequester | null = null;

function getDefaultBackend(): IRequester {
  if (defaultBackend) return defaultBackend;
  defaultBackend = isServer() ? new ServerApiService({ useSecureApi: true }) : new ClientApiService();
  return defaultBackend;
}

export interface IRequester {
  request: <T = unknown>(config: RequestOptions) => Promise<ApiResponse<T>>;
}

function isErrorObject(value: unknown): value is { error?: { message?: string } } {
  return typeof value === 'object' && value !== null && 'error' in (value as object);
}

/**
 * AbstractService
 *
 * Base class for all service modules.
 *
 * It centralizes shared backend operations and provides a single HTTP gateway
 * (via `ServerApiService` on the server and `ClientApiService` on the client).
 */
export abstract class AbstractService {
  protected http: IRequester;
  protected logger: ILogger;
  protected endpoints = new Map<EndpointKey, string>(Object.entries(ENDPOINTS) as Array<[EndpointKey, string]>);

  protected _instance!: AbstractService; // For singleton pattern

  static getInstance<T extends AbstractService>(this: new (backend?: IRequester) => T, backend?: IRequester): T {
    if (!(this as any)._instance) {
      (this as any)._instance = new this(backend);
    }
    return (this as any)._instance;
  }

  /**
   * Legacy alias for backward compatibility.
   */
  public get backend(): IRequester {
    return this.http;
  }
  public set backend(value: IRequester) {
    this.http = value;
  }

  constructor(backend?: IRequester) {
    this.http = backend ?? getDefaultBackend();
    this.logger = createLogger(this.constructor.name);
  }

  /**
   * Register/override endpoints for this service.
   */
  protected registerEndpoints(endpoints: Partial<Record<EndpointKey, string>>): void {
    Object.entries(endpoints).forEach(([key, value]) => {
      if (key && value) {
        this.endpoints.set(key as EndpointKey, value);
      }
    });
  }

  /**
   * Get a previously registered endpoint by key.
   */
  protected endpoint(key: EndpointKey, params?: Record<string, string | number>): string {
    const template = this.endpoints.get(key);
    if (!template) {
      throw new Error(`Endpoint not found for key: ${key}`);
    }

    let endpoint = template;
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        endpoint = endpoint.replace(`:${paramKey}`, String(paramValue));
      });
    }

    return this.normalizePath(endpoint);
  }

  /**
   * Normalize API path to ensure it is always rooted (prefix with `/`).
   */
  protected normalizePath(path: string): string {
    const clean = path.replace(/^\/+|\/+$/g, '');
    return clean ? `/${clean}` : '/';
  }

  /**
   * Ensure all responses follow the V3 API response shape.
   */
  protected async request<T = unknown>(config: RequestOptions): Promise<ApiResponse<T>> {
    const normalizedUrl = this.normalizePath(config.url as string);

    this.logger.debug({
      msg: 'AbstractService request',
      method: config.method,
      url: normalizedUrl,
    });

    const response = await this.http.request<T>({
      ...config,
      url: normalizedUrl,
    });

    this.logger.debug({
      msg: 'AbstractService response',
      method: config.method,
      url: normalizedUrl,
      response: JSON.stringify(response)?.substring(0, 500) + '...', // Truncate long responses
    });

    // BaseHttpClient already normalizes list responses (items → data).
    // Most services want the payload directly, not the wrapper.
    const payload = (response as any)?.data ?? response;
    return payload as ApiResponse<T>;
  }

  protected async get<T = unknown>(url: string, params?: QueryParams, config?: RequestOptions) {
    return this.request<T>({ method: 'get', url, params, ...config });
  }

  protected async post<T = unknown>(url: string, data?: unknown, config?: RequestOptions) {
    return this.request<T>({ method: 'post', url, data, ...config });
  }

  protected async put<T = unknown>(url: string, data?: unknown, config?: RequestOptions) {
    return this.request<T>({ method: 'put', url, data, ...config });
  }

  protected async patch<T = unknown>(url: string, data?: unknown, config?: RequestOptions) {
    return this.request<T>({ method: 'patch', url, data, ...config });
  }

  protected async delete<T = unknown>(url: string, config?: RequestOptions) {
    return this.request<T>({ method: 'delete', url, ...config });
  }

  /**
   * Assert the response does not contain an error object.
   */
  protected assertSuccess<T>(response: ApiResponse<T>): ApiResponse<T> {
    const data = response.data;
    if (isErrorObject(data)) {
      throw new Error(data.error?.message || 'API Error');
    }
    return response;
  }
}

export default AbstractService;
